import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Plus, Search, Filter, MapPin, Bot, Eye, Edit2,
  FileText, Loader2, ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { BlogPostDialog } from "./BlogPostDialog";

const T = {
  navy: "#002868",
  red: "#B22234",
  gold: "#C5A55A",
  green: "#16a34a",
  border: "rgba(27,27,27,0.12)",
};
const serif = "'Instrument Serif', Georgia, serif";
const sans = "'Instrument Sans', system-ui, sans-serif";

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  draft:            { bg: "rgba(27,27,27,0.08)",  text: "#555" },
  pending_approval: { bg: "rgba(234,179,8,0.15)", text: "#a16207" },
  approved:         { bg: "rgba(22,163,74,0.1)",  text: "#16a34a" },
  published:        { bg: "rgba(0,40,104,0.1)",   text: "#002868" },
  denied:           { bg: "rgba(178,34,52,0.1)",  text: "#B22234" },
  archived:         { bg: "rgba(27,27,27,0.06)",  text: "#888" },
};

const CONTENT_TYPES = [
  { value: "", label: "All Types" },
  { value: "article", label: "Article" },
  { value: "data_insight", label: "Data Insight" },
  { value: "news_commentary", label: "News Commentary" },
  { value: "state_law_analysis", label: "Law Analysis" },
  { value: "hospital_spotlight", label: "Hospital Spotlight" },
  { value: "cost_comparison", label: "Cost Comparison" },
  { value: "how_to_guide", label: "How-To Guide" },
];

export function BlogPostsPanel() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [editingPost, setEditingPost] = useState<any | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const { data: posts = [], isLoading } = useQuery({
    queryKey: ["admin_all_blog_posts", statusFilter, typeFilter],
    queryFn: async () => {
      let q = supabase
        .from("blog_posts" as any)
        .select("*")
        .order("created_at", { ascending: false });

      if (statusFilter) q = q.eq("status", statusFilter);
      if (typeFilter) q = q.eq("content_type", typeFilter);

      const { data, error } = await q;
      if (error) throw error;
      return data as any[];
    },
  });

  const publishMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("blog_posts" as any)
        .update({
          status: "published",
          publish_date: new Date().toISOString(),
        } as any)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin_all_blog_posts"] });
    },
  });

  // Filter by search term
  const filteredPosts = posts.filter(p => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return p.title?.toLowerCase().includes(term) ||
           p.slug?.toLowerCase().includes(term) ||
           p.state_code?.toLowerCase().includes(term);
  });

  return (
    <div>
      {/* Filter bar */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10, marginBottom: 20, flexWrap: "wrap",
      }}>
        {/* Search */}
        <div style={{
          display: "flex", alignItems: "center", gap: 6,
          padding: "8px 12px", border: `1px solid ${T.border}`, borderRadius: 8,
          backgroundColor: "#fff", flex: "1 1 200px", maxWidth: 320,
        }}>
          <Search size={14} style={{ color: "rgba(27,27,27,0.3)" }} />
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search posts..."
            style={{
              border: "none", outline: "none", flex: 1,
              fontSize: 13, fontFamily: sans, backgroundColor: "transparent",
            }}
          />
        </div>

        {/* Status filter */}
        <div style={{ position: "relative" }}>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            style={{
              padding: "8px 32px 8px 12px", border: `1px solid ${T.border}`,
              borderRadius: 8, fontSize: 13, fontFamily: sans,
              backgroundColor: "#fff", cursor: "pointer", appearance: "none",
            }}
          >
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="pending_approval">Pending</option>
            <option value="approved">Approved</option>
            <option value="published">Published</option>
            <option value="denied">Denied</option>
            <option value="archived">Archived</option>
          </select>
          <ChevronDown size={12} style={{
            position: "absolute", right: 10, top: "50%",
            transform: "translateY(-50%)", pointerEvents: "none",
            color: "rgba(27,27,27,0.4)",
          }} />
        </div>

        {/* Content type filter */}
        <div style={{ position: "relative" }}>
          <select
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            style={{
              padding: "8px 32px 8px 12px", border: `1px solid ${T.border}`,
              borderRadius: 8, fontSize: 13, fontFamily: sans,
              backgroundColor: "#fff", cursor: "pointer", appearance: "none",
            }}
          >
            {CONTENT_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <ChevronDown size={12} style={{
            position: "absolute", right: 10, top: "50%",
            transform: "translateY(-50%)", pointerEvents: "none",
            color: "rgba(27,27,27,0.4)",
          }} />
        </div>

        {/* Create button */}
        <Button
          onClick={() => setIsCreating(true)}
          style={{
            backgroundColor: T.navy, color: "#fff",
            fontFamily: sans, fontSize: 13, fontWeight: 600,
            marginLeft: "auto",
          }}
        >
          <Plus size={14} /> New Post
        </Button>
      </div>

      {/* Post count */}
      <p style={{
        fontSize: 12, color: "rgba(27,27,27,0.4)", fontFamily: sans, marginBottom: 12,
      }}>
        {filteredPosts.length} post{filteredPosts.length !== 1 ? "s" : ""}
      </p>

      {/* Loading */}
      {isLoading && (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Loader2 size={24} style={{ color: T.navy, animation: "spin 1s linear infinite" }} />
        </div>
      )}

      {/* Posts list */}
      {!isLoading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {filteredPosts.map(post => {
            const sc = STATUS_COLORS[post.status] || STATUS_COLORS.draft;
            return (
              <div
                key={post.id}
                style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "14px 16px", backgroundColor: "#fff",
                  border: `1px solid ${T.border}`, borderRadius: 8,
                  cursor: "pointer", transition: "all 0.1s",
                }}
                onClick={() => setEditingPost(post)}
              >
                {/* Icon */}
                <FileText size={16} style={{ color: T.navy, flexShrink: 0, opacity: 0.5 }} />

                {/* Status badge */}
                <span style={{
                  padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700,
                  fontFamily: sans, textTransform: "uppercase", letterSpacing: "0.04em",
                  backgroundColor: sc.bg, color: sc.text, flexShrink: 0,
                }}>
                  {post.status.replace("_", " ")}
                </span>

                {/* State */}
                {post.state_code && (
                  <span style={{
                    display: "inline-flex", alignItems: "center", gap: 2,
                    fontSize: 10, fontWeight: 600, color: "#8B7730",
                    backgroundColor: "rgba(197,165,90,0.12)",
                    padding: "2px 6px", borderRadius: 4, fontFamily: sans, flexShrink: 0,
                  }}>
                    <MapPin size={9} /> {post.state_code}
                  </span>
                )}

                {/* Content type */}
                <span style={{
                  fontSize: 10, color: "rgba(27,27,27,0.4)", fontFamily: sans, flexShrink: 0,
                }}>
                  {post.content_type}
                </span>

                {/* Title */}
                <span style={{
                  flex: 1, fontFamily: sans, fontSize: 13, fontWeight: 500,
                  color: "#1B1B1B", overflow: "hidden", textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}>
                  {post.title}
                </span>

                {/* AI badge */}
                {post.ai_generated && <Bot size={12} style={{ color: "#7c3aed", flexShrink: 0 }} />}

                {/* Date */}
                <span style={{
                  fontSize: 11, color: "rgba(27,27,27,0.35)", fontFamily: sans, flexShrink: 0,
                }}>
                  {post.publish_date
                    ? new Date(post.publish_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                    : new Date(post.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                  }
                </span>

                {/* Actions */}
                <div style={{ display: "flex", gap: 4, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                  {post.status === "approved" && (
                    <button
                      onClick={() => publishMutation.mutate(post.id)}
                      style={{
                        padding: "3px 8px", border: `1px solid rgba(0,40,104,0.3)`,
                        borderRadius: 4, backgroundColor: "transparent",
                        color: T.navy, fontSize: 10, fontWeight: 600,
                        fontFamily: sans, cursor: "pointer",
                      }}
                    >
                      Publish
                    </button>
                  )}
                  <button
                    onClick={() => window.open(`/blog/${post.slug}`, "_blank")}
                    style={{
                      padding: "3px 6px", border: "none", background: "none",
                      color: "rgba(27,27,27,0.35)", cursor: "pointer",
                    }}
                  >
                    <Eye size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create / Edit dialog */}
      {(isCreating || editingPost) && (
        <BlogPostDialog
          post={editingPost}
          onClose={() => { setEditingPost(null); setIsCreating(false); }}
          onSaved={() => {
            setEditingPost(null);
            setIsCreating(false);
            queryClient.invalidateQueries({ queryKey: ["admin_all_blog_posts"] });
          }}
        />
      )}
    </div>
  );
}
